import '../ServiceableUrls.less';
import { Form, Select, Tooltip, Button, Input } from 'antd';
import React, { useState } from 'react';
import { UrlCluster } from '../../urlClusters/types';
import { useFetchWebsiteIndexEntriesQuery } from '../../websiteIndexEntry/service';
import { validateInputUrls } from '../../utils';
import { useAddServiceableUrlsInClusterMutation } from '../service';
import { urlClustersApi } from '../../urlClusters/service';

const { TextArea } = Input;
const { Option } = Select;

const initialUrlsInputState = {
    urls: '',
    validationError: '',
};

type Props = {
    websiteGuid: string;
    clusters: UrlCluster[];
    selectedCluster: string;
    maximumAllowedUrls: number;
    onClusterChange: (newCluster: string) => void;
    onAddCluster: () => void;
    onFinish: () => void;
};

export default function AddUrlsStep({ websiteGuid, clusters, selectedCluster, maximumAllowedUrls, onClusterChange, onAddCluster, onFinish }: Props) {
    const [urlsInputState, setUrlsInputState] = useState(initialUrlsInputState);
    const [clusterValidationError, setClusterValidationError] = useState<string>('');

    const { data: websiteIndexEntries, isFetching: isLoadingWebsiteIndexEntries } = useFetchWebsiteIndexEntriesQuery();
    const [addServiceableUrlsInCluster, { isLoading: isAddingServiceableUrls }] = useAddServiceableUrlsInClusterMutation();
    const { isFetching: isFetchingUrlClusters } = urlClustersApi.endpoints.fetchUrlClusters.useQueryState({ w_guid: websiteGuid });

    const handleClusterChange = (newCluster: string) => {
        setClusterValidationError('');
        onClusterChange(newCluster);
    };

    const handleUrlsInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setUrlsInputState({ urls: event.currentTarget.value, validationError: '' });
    };

    const handleAddButtonClicked = async () => {
        if (!selectedCluster) {
            setClusterValidationError('Please select a page group');
            return;
        }

        const selectedWebsiteIndexEntry = websiteIndexEntries?.find((websiteIndexEntry) => websiteIndexEntry.guid === websiteGuid);

        if (selectedWebsiteIndexEntry) {
            const [inputUrls, inputUrlsValidationErrors] = validateInputUrls(urlsInputState.urls, selectedWebsiteIndexEntry.domain);

            if (inputUrlsValidationErrors.some((error) => error) || inputUrls.length === 0) {
                setUrlsInputState({ ...urlsInputState, validationError: `Please add valid URLs from domain: ${selectedWebsiteIndexEntry.domain}` });
                return;
            }

            if (inputUrls.length > maximumAllowedUrls) {
                setUrlsInputState({ ...urlsInputState, validationError: 'URLs exceeded limit' });
                return;
            }

            const uniqueUrls = inputUrls.filter((url, index) => inputUrls.indexOf(url) === index);

            try {
                await addServiceableUrlsInCluster({ w_guid: websiteGuid, cluster_guid: selectedCluster, urls: uniqueUrls }).unwrap();

                onFinish();
            } catch (error) {
                setUrlsInputState({ ...urlsInputState, validationError: 'Something went wrong! Please try again or contact support.' });
            }
        }
    };

    const showSelectedCluster = !!selectedCluster && clusters.map((cluster) => cluster.guid).includes(selectedCluster);

    return (
        <>
            <div>
                <h3>Page Group</h3>
                <label
                    className="form-label"
                    htmlFor="select-cluster"
                >
                    Select an existing page group, or add a new one
                </label>
            </div>

            <Form.Item
                validateStatus={clusterValidationError ? 'error' : 'validating'}
                help={clusterValidationError ? clusterValidationError : ''}
            >
                <div className="select-cluster-container">
                    <Select
                        id="select-cluster"
                        placeholder="Select page group"
                        value={showSelectedCluster ? selectedCluster : undefined}
                        onChange={handleClusterChange}
                        disabled={isFetchingUrlClusters}
                    >
                        {clusters.map((cluster) => (
                            <Option
                                key={cluster.guid}
                                value={cluster.guid}
                            >
                                {cluster.title}
                            </Option>
                        ))}
                    </Select>

                    <Tooltip
                        placement="topRight"
                        title="Add page group"
                    >
                        <Button
                            type="primary"
                            icon="plus"
                            className="add-cluster-button"
                            onClick={onAddCluster}
                        />
                    </Tooltip>
                </div>
            </Form.Item>

            <div>
                <h3>URLs</h3>
                <label
                    className="form-label"
                    htmlFor="textarea"
                >
                    Add new URLs in the selected page group (maximum {maximumAllowedUrls} URLs, comma separated or one URL per line).
                </label>
            </div>

            <Form.Item
                validateStatus={urlsInputState.validationError ? 'error' : 'validating'}
                help={urlsInputState.validationError ? urlsInputState.validationError : ''}
            >
                <TextArea
                    id="textarea"
                    placeholder="https://yourdomain.com/about"
                    value={urlsInputState.urls}
                    onChange={handleUrlsInputChange}
                    rows={6}
                    cols={100}
                    disabled={isLoadingWebsiteIndexEntries}
                ></TextArea>
            </Form.Item>

            <div className="form-button-group">
                <Button
                    className="action-add-button"
                    type="primary"
                    size="small"
                    onClick={handleAddButtonClicked}
                    disabled={isLoadingWebsiteIndexEntries}
                    loading={isAddingServiceableUrls}
                >
                    Add URLs
                </Button>
            </div>
        </>
    );
}
